/* eslint-disable react/no-unescaped-entities, react-hooks/set-state-in-effect */
/**
 * CockpitScoreCard — Dark Luxury widget en haut de sidebar
 * ========================================================
 * "Que dois-je faire maintenant ?" — Affiche le nombre d'actions urgentes
 * du jour avec une phrase humaine. Visible sur toutes les pages du SaaS
 * connecté pour ramener le Manager à son cockpit chaque fois qu'il navigue.
 *
 * Réutilise /api/cockpit-ai/briefing (cache backend 60s).
 * Apparaît uniquement si priorities_count > 0.
 *
 * Cible : role admin/agent — sidebar desktop + mobile drawer.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, CheckCircle2 } from 'lucide-react';
import api from '../../lib/api';

const FETCH_INTERVAL_MS = 5 * 60 * 1000; // 5 min — backend a déjà un cache 60s

export default function CockpitScoreCard({ basePath = '/admin' }) {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);

    const fetchScore = useCallback(async () => {
        try {
            const res = await api.get('/cockpit-ai/briefing');
            setData(res.data);
        } catch {
            setData(null);
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        fetchScore();
        const id = setInterval(fetchScore, FETCH_INTERVAL_MS);
        return () => clearInterval(id);
    }, [fetchScore]);

    // Skeleton compact avant 1er chargement
    if (!loaded) {
        return (
            <div
                aria-hidden="true"
                style={{
                    margin: '12px',
                    padding: '12px 14px',
                    background: 'var(--homly-bg-elevated)',
                    border: '1px solid var(--homly-border-subtle)',
                    borderRadius: 'var(--homly-radius-md)',
                    height: 64,
                    opacity: 0.6,
                }}
            />
        );
    }

    const count = data?.priorities_count || 0;
    const isZero = count === 0;
    const exposedAmount = data?.exposed_amount || 0;

    // Helper pour formater le montant exposé
    const fmtAmount = (n) => {
        if (n >= 1000) return `${Math.round(n / 1000)} k€`;
        return `${Math.round(n)} €`;
    };

    const handleClick = () => {
        // Emmène toujours le manager vers la LISTE complète des priorités du cockpit
        // (et non vers un seul mandat) — répond au besoin « voir les N actions à traiter ».
        if (basePath === '/admin') {
            navigate('/admin?tab=actions');
        } else {
            navigate(basePath);
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            data-testid="cockpit-score-card"
            aria-label={isZero ? 'Aucune urgence' : `${count} actions urgentes à traiter`}
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                width: 'calc(100% - 24px)',
                margin: '12px',
                padding: '12px 14px',
                background: isZero
                    ? 'rgba(34, 211, 162, 0.08)'
                    : 'linear-gradient(135deg, rgba(104, 23, 147, 0.18) 0%, rgba(255, 138, 61, 0.10) 100%), var(--homly-bg-elevated)',
                border: isZero
                    ? '1px solid rgba(34, 211, 162, 0.25)'
                    : '1px solid var(--homly-purple-border)',
                borderRadius: 'var(--homly-radius-md)',
                boxShadow: isZero ? 'none' : '0 2px 12px rgba(168, 85, 247, 0.15)',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all var(--homly-transition-fast)',
                fontFamily: 'var(--homly-font-body)',
            }}
            onMouseEnter={(e) => {
                if (!isZero) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(168, 85, 247, 0.25)';
                }
            }}
            onMouseLeave={(e) => {
                if (!isZero) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(168, 85, 247, 0.15)';
                }
            }}
        >
            {/* Top row : kicker + arrow */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {isZero ? (
                        <CheckCircle2 size={12} style={{ color: 'var(--homly-priority-ok)' }} />
                    ) : (
                        <Sparkles size={12} style={{ color: 'var(--homly-orange)' }} />
                    )}
                    <span
                        style={{
                            fontSize: 9,
                            letterSpacing: '0.16em',
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            color: isZero ? 'var(--homly-priority-ok)' : 'var(--homly-orange)',
                            fontFamily: 'var(--homly-font-display)',
                        }}
                    >
                        {isZero ? 'Cockpit clair' : "À traiter aujourd'hui"}
                    </span>
                </div>
                <ArrowRight size={12} strokeWidth={2.2} style={{ color: 'var(--homly-text-tertiary)', opacity: isZero ? 0.4 : 0.8 }} />
            </div>

            {/* Big counter + label */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span
                    style={{
                        fontFamily: 'var(--homly-font-mono)',
                        fontSize: 26,
                        fontWeight: 700,
                        letterSpacing: '-0.02em',
                        lineHeight: 1,
                        color: isZero ? 'var(--homly-priority-ok)' : 'var(--homly-text-primary)',
                    }}
                >
                    {count}
                </span>
                <span style={{ fontSize: 12, color: 'var(--homly-text-secondary)' }}>
                    {isZero
                        ? 'urgence — bravo'
                        : count > 1
                            ? 'actions urgentes'
                            : 'action urgente'}
                </span>
            </div>

            {/* Sous-titre humain : pipeline exposé ou message */}
            {!isZero && exposedAmount > 0 && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 10.5,
                        color: 'var(--homly-text-tertiary)',
                    }}
                >
                    <span style={{ fontFamily: 'var(--homly-font-mono)', color: 'var(--homly-orange)', fontWeight: 700 }}>
                        {fmtAmount(exposedAmount)}
                    </span>
                    <span>d'honoraires en jeu</span>
                </div>
            )}
            {isZero && (
                <div style={{ fontSize: 10.5, color: 'var(--homly-text-tertiary)' }}>
                    Aucun mandat à risque. Profitez-en pour prospecter.
                </div>
            )}
        </button>
    );
}
